import React, { useState } from 'react'
import Image, { ImageProps } from '@components/atoms/Image'
import ColorDot from '@components/atoms/ColorDot'
import { VariantMetafields } from '@interfaces/product'
import Icon from '../atoms/Icon'

interface ImageContainerProps {
  img: ImageProps
  imgAlt: string
  imgTitle: string
  srcWidths: {
    sm: number
    md: number
    lg: number
    xl: number
    '2xl': number
  }
  badges?: VariantMetafields['badges']
}

const ImageContainer: React.FC<ImageContainerProps> = ({ img, badges, srcWidths, imgTitle, imgAlt }) => {
  const [easterEggCopy, setEasterEggCopy] = useState<number[]>([]);

  const renderBadges = (badges?: VariantMetafields['badges']) => {
    if (!badges || badges.length === 0) return null

    return (
      <div className="absolute z-1 left-2 top-7 flex items-center flex-wrap gap-2">
        {badges.map((badge, index) => (
          <div
            key={index}
            className="rounded-full px-3 py-px body-xxs flex items-center h-4.5"
            role="button"
            style={{
              color: badge.copyColor || '#000000',
              background: badge.gradient || badge.bgColor,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if(badge.easterEggCopy){
                setEasterEggCopy(prev =>
                prev.includes(index)
                  ? prev.filter(i => i !== index) : [...prev, index]
                );
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if(badge.easterEggCopy){
                  setEasterEggCopy(prev =>
                    prev.includes(index)
                      ? prev.filter(i => i !== index) : [...prev, index]
                  );
                }
              }
            }}
            aria-expanded={badge.easterEggCopy ? easterEggCopy.includes(index) : undefined}
            aria-label={badge.easterEggCopy ? `${badge.name} badge, click for more information` : badge.name}
          >
            {easterEggCopy.includes(index) && badge.easterEggCopy ? (
              badge.easterEggCopy
            ) : (
              <>
                {badge.image ? (
                  <Image src={badge.image} alt={badge.name} classes="h-7" />
                ): (
                  badge.name
                )
                }
              </>

            )}
            &nbsp;
            {badge.easterEggCopy && !easterEggCopy.includes(index) &&(
              <Icon name="question" className={badge.copyColor || '#000000'} aria-hidden="true"/>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="shadow-window cursor-pointer relative rounded overflow-hidden">
      <div className="absolute z-2 top-0 left-0 right-0 bg-gradient-metal border-grey-2">
        <div className="flex items-center gap-x-1 py-1.5 px-1 md:p-1.5">
          <ColorDot colorType="gray" />
          <ColorDot colorType="gray" />
          <ColorDot colorType="green" />
          {imgTitle && (
            <p className="body-xxs font-medium absolute inset-0 flex items-center justify-center">{imgTitle}</p>
          )}
        </div>
      </div>
      <div className="relative">
        {renderBadges(badges)}
        <Image {...img} alt={imgAlt} srcWidths={srcWidths} classes="rounded-t-large" />
      </div>
    </div>
  )
}

export default ImageContainer
